import { EF_CLICK_STRIP_PARAMS, EF_TID_COOKIE, EF_TID_PARAM, EF_TID_PATTERN } from './constants';

/**
 * Isomorphic helpers for carrying the Everflow transaction id across our domains.
 * No DOM or Next.js dependencies except in `withEfTid`, which is client-only by design.
 */

export const isValidEfTransactionId = (value: string | null | undefined): value is string =>
  typeof value === 'string' && EF_TID_PATTERN.test(value);

/**
 * Registrable domain = last two labels: `checkout.pickleballtournamentsdev.net` →
 * `pickleballtournamentsdev.net`, `dev.pickleballbrackets.dev` → `pickleballbrackets.dev`.
 * A port and a trailing dot are ignored; single-label hosts (`localhost`) come back as-is.
 * None of our domains sit under a multi-part public suffix (`.co.uk`), so two labels is enough.
 */
export const getRegistrableDomain = (hostname: string): string => {
  const bareHost = hostname.toLowerCase().split(':')[0] ?? '';
  const labels = bareHost.replace(/\.$/, '').split('.');
  return labels.slice(-2).join('.');
};

/**
 * Builds the list of registrable domains the decorator treats as "ours" from the
 * `NEXT_PUBLIC_*_URL` env values. Empty / invalid values are skipped, duplicates collapsed.
 */
export const buildEverflowDomains = (urls: ReadonlyArray<string | null | undefined>): string[] => {
  const domains = new Set<string>();
  for (const value of urls) {
    if (!value) continue;
    try {
      domains.add(getRegistrableDomain(new URL(value).hostname));
    } catch {
      // not a URL — skip
    }
  }
  return [...domains];
};

export const isOurHost = (hostname: string, domains: readonly string[]): boolean => {
  const host = hostname.toLowerCase();
  return domains.some((domain) => host === domain || host.endsWith(`.${domain}`));
};

export interface DecorateHrefOptions {
  currentHostname: string;
  domains: readonly string[];
}

/**
 * Returns `href` with `_ef_transaction_id` set when it leads from the current host to
 * another one of our hosts; `null` when there is nothing to do (same host, foreign host,
 * non-http scheme, relative href, invalid id, or the id is already there).
 */
export const decorateHref = (
  href: string,
  tid: string,
  { currentHostname, domains }: DecorateHrefOptions
): string | null => {
  if (!isValidEfTransactionId(tid)) return null;

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null; // relative href → same host
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const target = url.hostname.toLowerCase();
  if (target === currentHostname.toLowerCase()) return null;
  if (!isOurHost(target, domains)) return null;
  if (url.searchParams.get(EF_TID_PARAM) === tid) return null;

  url.searchParams.set(EF_TID_PARAM, tid);
  return url.toString();
};

export const readCookie = (cookieString: string, name: string): string | null => {
  if (!cookieString) return null;
  for (const part of cookieString.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName !== name) continue;
    const raw = rest.join('=');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
};

/**
 * URL first, then our cookie — the lookup order Everflow recommends. A new id on the
 * URL therefore always wins over an older one in the cookie (last-touch).
 */
export const resolveEfTid = (search: string, cookieString: string): string | null => {
  const fromUrl = new URLSearchParams(search).get(EF_TID_PARAM);
  if (isValidEfTransactionId(fromUrl)) return fromUrl;

  const fromCookie = readCookie(cookieString, EF_TID_COOKIE);
  return isValidEfTransactionId(fromCookie) ? fromCookie : null;
};

/**
 * Client-side only. For programmatic navigations (`router.push`, `window.location.href`,
 * `window.open`) that lead to another one of our domains — `<a>` clicks are handled by
 * the `EverflowLinkDecorator` and need nothing.
 */
export const withEfTid = (url: string, domains: readonly string[]): string => {
  if (typeof window === 'undefined') return url;

  const tid = resolveEfTid(window.location.search, document.cookie);
  if (!tid) return url;

  return decorateHref(url, tid, { currentHostname: window.location.hostname, domains }) ?? url;
};

const EF_ID_PATTERN = /^\d{1,12}$/;

/**
 * A direct-link landing: a partner's `oid` + `affid` (Everflow integer ids) and no transaction id.
 * Everflow has recorded nothing for it yet — that is what the server-side click is for.
 */
export const isDirectLinkLanding = (searchParams: URLSearchParams): boolean =>
  !isValidEfTransactionId(searchParams.get(EF_TID_PARAM)) &&
  EF_ID_PATTERN.test(searchParams.get('oid') ?? '') &&
  EF_ID_PATTERN.test(searchParams.get('affid') ?? '');

/** The landing URL without Everflow's click parameters (`fbclid` / `gclid` are kept). */
export const stripEverflowClickParams = (url: URL): URL => {
  const clean = new URL(url.toString());
  for (const param of EF_CLICK_STRIP_PARAMS) clean.searchParams.delete(param);
  return clean;
};
